import { GeoLocationEvent } from "./interface";
import { ServiceInterface } from "../service.interface";
import { StorageService } from "../storage";
import { ApiService } from "../api";
import { PermissionService } from "../permission";
import { Types, HeadingUpdate } from "@citychallenge/common";
import { createContext } from "react";
import { Environment } from "../../environment";
import { GeolocationAdapterInterface } from "./geolocation-adapter.interface";
import { AppStateAdapter } from "../app-state-adapter";

export type LocationObserver = (event: GeoLocationEvent) => void;

export const GeoServiceContext = createContext<GeoService>(null as any);

export class GeoService implements ServiceInterface {
  public context = GeoServiceContext;

  constructor(
    private apiService: ApiService,
    private storageService: StorageService,
    private permissionService: PermissionService,
    private geolocationAdapter: GeolocationAdapterInterface,
    private appState: AppStateAdapter
  ) {}

  public lastKnownLocation = this.storageService.getPreference<
    Types["GeoLocation"]
  >("_LAST_KNOWN_LOCATION");

  public async serviceWillMount() {
    await this.lastKnownLocation.ready$;
  }

  public async serviceWillBoot() {
    if (this.permissionService.location === "granted") {
      this.retrieveCurrentLocation();
    } else {
      this.retrieveLocationEstimate();
    }
  }

  public serviceDidBecomeBackground = () => {
    this.stopObserving();
  };

  /**
   * Set lastKnownLocation based on an IP estimation using a server call.
   * @returns Promise that returns when estimate is complete
   */
  public async retrieveLocationEstimate() {
    // Only retrieve location if there was not a previous location known
    if (this.lastKnownLocation.value) return;

    const { data } = await this.apiService.clientPublic.getEstimatedLocation();

    if (!this.lastKnownLocation.value) {
      this.lastKnownLocation.setValue({
        lat: data.lat,
        lng: data.lon,
      });
    }
  }

  /**
   * Set lastKnownLocation based on a accurate estimation using the react native
   * location service
   */
  public async retrieveCurrentLocation() {
    if (this.appState.currentState !== "active") return;

    this.geolocationAdapter.getCurrentPosition((position) => {
      const { latitude, longitude } = position.coords;
      this.lastKnownLocation.setValue({ lat: latitude, lng: longitude });
    });
  }

  public async stopObserving() {
    this.geolocationAdapter.stopObserving();
  }

  public lastHeadingUpdate?: HeadingUpdate;
  public lastLocationUpdate?: GeoLocationEvent;

  /**
   * Get lastLocationUpdate, but only if lastLocationUpdate was received
   * within the last 5 minutes
   */
  public get recentLocationUpdate(): GeoLocationEvent | undefined {
    const { lastLocationUpdate } = this;

    if (!lastLocationUpdate?.timestamp) return undefined;

    const date = new Date().getTime();
    /**
     * Timestamp is reported diffrently on iOS & Android, on Android it's
     * reported in Milliseconds, and on iOS in seconds.
     * @see https://github.com/react-native-mapbox-gl/maps/issues/204
     */
    const timestamp =
      Environment.OS === "ios"
        ? lastLocationUpdate.timestamp * 1000
        : lastLocationUpdate.timestamp;

    const diffSeconds = (date - timestamp) / 1000;
    const maxAge = 5 * 60;

    return diffSeconds <= maxAge ? lastLocationUpdate : undefined;
  }
}
